import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BaseService } from '@services/base-service.service';
import { BaseResponse } from '@shared/base/base-response';
import { API } from '@config/constants/api.constant';
import {
  CounterList,
  IDistributorByScopeResult,
  IRevisionNoteTargetDistributor,
  ITargetDistributorArea,
  ITargetDistributorDistributor,
  ITargetDistributorProduct,
  IProductForConversion,
  IProductConversionVariant,
  ITargetDistributorSubmitRequest,
  ITargetDistributorSubmitVerificationRequest,
  IConversionProductOption,
  IDetailEditTargetDistributor,
} from './target-distributor.interface';
import { EnumTargetDistributorStatus } from './target-distributor.enum';
import {
  ITargetDistributorMonitoring__AreaFilter,
  ITargetDistributorMonitoring__Distributor,
  ITargetDistributorMonitoring__DistributorDetail,
  ITargetDistributorMonitoring__FilterParams,
  ITargetDistributorMonitoring__Invoice,
  ITargetDistributorMonitoring__InvoiceParams,
  ITargetDistributorMonitoring__ListParams,
  ITargetDistributorMonitoring__OtherRewards,
  ITargetDistributorMonitoring__PotentialPo,
  ITargetDistributorMonitoring__PotentialPoParams,
  ITargetDistributorMonitoring__Product,
  ITargetDistributorMonitoring__ProductParams,
  ITargetDistributorMonitoring__Summary,
} from './target-distributor-monitoring.interface';
import {} from // getMonitoringAreaFilterMock, // [MAI-2598] endpoint detail/filter-area/{id} sudah aktif
// getMonitoringDistributorDetailMock, // [MAI-2601] endpoint detail/distributor-detail/{targetDistributorId}/{distributorId} sudah aktif
// getMonitoringDistributorListMock, // [MAI-2598] endpoint list/{id} sudah aktif
// getMonitoringInvoiceListMock, // [MAI-2604] endpoint detail/distributor-detail/invoice-list/{...} sudah aktif
// getMonitoringOtherRewardsMock, // [MAI-2725] endpoint detail/distributor-detail/other-rewards/{...} sudah aktif
// getMonitoringPotentialPoListMock, // [MAI-2608] endpoint detail/distributor-detail/potential-po/{...} sudah aktif
// getMonitoringProductListMock,
// getMonitoringSummaryMock, // [MAI-2598] endpoint summary/{id} sudah aktif
'./target-distributor-monitoring.mock';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CardHeaderCounterMapperService } from '@shared/components/card/card-header/card-header-counter/card-header-counter-mapper.service';
import { PageInfoService, PageLink } from '@metronic/layout';
import { IDetailTargetDistributor } from '@pages/sales-marketing/target-distributor/detail-target-distributor.interface';
import { RolePrivilegeService } from '@services/role-privilege.service';

@Injectable({
  providedIn: 'root',
})
export class TargetDistributorService {
  basePageUrl = '/sales-marketing/target-distributor/';

  // [MAI-1296] Flag terpisah dari `useMock` supaya mematikan mock monitoring nanti
  // tidak ikut membangkitkan mock detail.
  // [MAI-2598] Endpoint dimatikan mock-nya satu per satu begitu siap di dev-second:
  // summary/{id}, list/{id}, dan detail/filter-area/{id} sudah aktif, sisanya masih mock.
  // filter-area wajib ikut real: area_id dari mock tidak cocok dengan data list asli.
  // [MAI-2601] detail/distributor-detail/{targetDistributorId}/{distributorId} juga sudah aktif.
  // [MAI-2725] other-rewards, invoice-list, dan potential-po dicek langsung ke dev-second
  // (2026-08-26): rute hidup dan bentuk responsnya cocok dengan interface, mock dimatikan.
  // Tersisa product-list yang rutenya belum ada di dev-first maupun dev-second
  // (respons "No static resource ..."), jadi mocknya masih menyala.

  // Mock diberi jeda supaya berperilaku asinkron seperti panggilan HTTP asli: skeleton loader
  // sempat tampil dan `mat-paginator` (yang dibuat ulang saat finishLoading false -> true)
  // merender ulang tombol halamannya sesuai jumlah data terbaru.
  private _detail$ = new BehaviorSubject<IDetailTargetDistributor | null>(null);
  readonly detail$ = this._detail$.asObservable();
  private readonly _detailTrigger$ = new Subject<string>();

  constructor(
    private router: Router,
    private baseService: BaseService,
    private pageInfoService: PageInfoService,
    private cardHeaderCounterService: CardHeaderCounterMapperService,
    private rolePrivilegeService: RolePrivilegeService,
    private destroyRef: DestroyRef
  ) {
    this._detailTrigger$
      .pipe(
        tap(() => this._detail$.next(null)),
        switchMap((id) => this.baseService.getData<IDetailTargetDistributor>(API.TARGET_DISTRIBUTOR.DETAIL + id).pipe(filter((res) => res !== null))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        if (res?.success) this._detail$.next(res.data);
      });
  }

  getCounterList(params?: string) {
    let _endpoint = API.TARGET_DISTRIBUTOR.COUNT_LIST;
    if (params) _endpoint = _endpoint + params;

    return this.baseService.getData<CounterList>(_endpoint, { disableModalPopupError: true }).pipe(
      map((res) => {
        if (!res) return;
        if (res && res.success) {
          const _keyName = {
            active: 'Aktif',
            submitted: 'Diajukan',
            scheduled: 'Dijadwalkan',
            finished: 'Selesai',
            need_changed: 'Butuh Perbaikan',
          };
          return this.cardHeaderCounterService.mapToCardHeaderCounter(res.data, _keyName);
        }
      })
    );
  }

  getAreaList() {
    // [OLD - mock]
    // if (this.useMock) {
    //   return of(MOCK_TARGET_DISTRIBUTOR_AREA);
    // }

    return this.baseService
      .getData<ITargetDistributorArea[]>(API.TARGET_DISTRIBUTOR.LIST_AREA, { disableModalPopupError: true })
      .pipe(map((res) => (res && res.success ? res.data : [])));
  }

  getAreaNational(): Observable<string[]> {
    return this.baseService
      .getData<ITargetDistributorArea[]>(API.TARGET_DISTRIBUTOR.SETTING.AREA_NATIONAL, { disableModalPopupError: true })
      .pipe(map((res) => (res && res.success ? res.data.map((a) => a.id) : [])));
  }

  get initBreadcrumb() {
    return <PageLink[]>[
      {
        title: 'Sales & Marketing',
        path: '',
        isActive: false,
      },
      this.pageInfoService.BreadcrumbSeparator,
      {
        title: 'Target Distributor',
        path: '/sales-marketing/target-distributor/list',
        isActive: true,
      },
    ];
  }

  getProductList(params?: { type?: 'category' | 'brand' | 'variant'; string_filter?: string }): Observable<ITargetDistributorProduct[]> {
    const endpointMap = {
      category: API.TARGET_DISTRIBUTOR.SETTING.CATEGORIES,
      brand: API.TARGET_DISTRIBUTOR.SETTING.BRANDS,
      variant: API.TARGET_DISTRIBUTOR.SETTING.VARIANTS,
    };
    const resolvedType = params?.type ?? 'variant';
    const endpoint = endpointMap[resolvedType];
    return this.baseService.getData<ITargetDistributorProduct[]>(endpoint, { disableModalPopupError: true }).pipe(
      filter((res) => res !== null), // skip BehaviorSubject(null) initial emit
      map((res) => (res && res.success ? res.data.map((item) => ({ ...item, type: item.type ?? resolvedType })) : []))
    );
  }

  getDistributorList(params?: { string_filter?: string; page?: number; size?: number }): Observable<IDistributorByScopeResult> {
    return this.baseService
      .getData<ITargetDistributorDistributor[]>(
        API.TARGET_DISTRIBUTOR.SETTING.DISTRIBUTORS,
        { disableModalPopupError: true },
        {
          ...(params?.string_filter ? { string_filter: params.string_filter } : {}),
          page: params?.page ?? 0,
          size: params?.size ?? 20,
        }
      )
      .pipe(
        filter((res) => res !== null),
        map((res) => ({
          data: res?.success
            ? res.data.map((d) => ({
                ...d,
                area: d.area || this.parseAreaFromName(d.name),
              }))
            : [],
          totalPage: res?.total_page ?? 0,
          currentPage: res?.current_page ?? 0,
        }))
      );
  }

  private parseAreaFromName(name: string): string {
    const match = name?.match(/\(([^)]+)\)$/);
    return match ? match[1] : '';
  }

  getTargetLevelList() {
    // [OLD - mock]
    // if (this.useMock) {
    //   return of(MOCK_TARGET_LEVEL_LIST);
    // }

    return this.baseService
      .getData<{ enum_level: string; string_level: string; index: number }[]>(API.TARGET_DISTRIBUTOR.SETTING.TARGET_LEVEL, { disableModalPopupError: true })
      .pipe(
        filter((res) => res !== null),
        map((res) => (res && res.success ? res.data.filter((item) => item.index <= 5) : []))
      );
  }

  getTierTargetList(): Observable<{ enum_tier: string; string_level: string; index: number }[]> {
    // [OLD - mock]
    // if (this.useMock) {
    //   return of(MOCK_TIER_TARGET_LIST);
    // }

    return this.baseService.getData(API.TARGET_DISTRIBUTOR.SETTING.TIER_TARGET, { disableModalPopupError: true }).pipe(
      map((res: any) => {
        const data = res && res.success && Array.isArray(res.data) && res.data.length > 0 ? res.data : null;
        return data;
      })
    );
  }

  getDetailById(id: string): void {
    this._detailTrigger$.next(id);
  }

  getDistributorByScope(params: { scope_type: string; area_ids: string; string_filter: string; page?: number }): Observable<IDistributorByScopeResult> {
    // [OLD - mock]
    // if (this.useMock) {
    //   return of({ data: MOCK_DISTRIBUTOR_BY_SCOPE, totalPage: 1, currentPage: 0 });
    // }
    return this.baseService.getData<ITargetDistributorDistributor[]>(API.TARGET_DISTRIBUTOR.SETTING.DISTRIBUTOR_BY_SCOPE, undefined, params).pipe(
      filter((res) => res !== null),
      map((res) => ({
        data: res?.success ? res.data : [],
        totalPage: res?.total_page ?? 0,
        currentPage: res?.current_page ?? 0,
      }))
    );
  }

  getListVariantConversion(id: string) {
    return this.baseService.getData<IProductConversionVariant[]>(`${API.TARGET_DISTRIBUTOR.SETTING.GET_LIST_CONVERSION_VARIANT_PRODUCT}${id}`, { disableModalPopupError: true });
    // [OLD - mock]
    // return of({ data: MOCK_CONVERSION_VARIANTS[id] ?? [] });
  }

  mapInputSelectProductConversion(data: IProductForConversion[]): IConversionProductOption[] {
    return data.map((item) => ({
      value: item.variant_id,
      label: item.variant_name ?? item.variant_id,
      disabled: false,
      productType: item.type,
      categoryId: item.category_id,
      brandId: item.brand_id,
    }));
  }

  submit(payload: ITargetDistributorSubmitRequest): Observable<BaseResponse<{ id: string }>> {
    return this.baseService.postData<{ id: string }>(API.TARGET_DISTRIBUTOR.SUBMIT, payload);
  }

  private readonly FAKE_REVISION_NOTES = [
    'Mohon perbaiki target sesuai arahan management.',
    'Insentif tingkat terakhir bisa ditambah mengingat targetnya lumayan tinggi.',
    'Cakupan distributor perlu disesuaikan kembali dengan area penjualan.',
    'Periode program tumpang tindih dengan program lain, mohon direvisi.',
  ];

  // TODO: hapus override ini setelah backend seed revision_note asli untuk program NEED_CHANGED
  private fakeRevisionNoteForDemo(detail: IDetailEditTargetDistributor): IDetailEditTargetDistributor {
    if (detail.status_enum !== EnumTargetDistributorStatus.NEED_CHANGED) return detail;
    const randomNote = () => this.FAKE_REVISION_NOTES[Math.floor(Math.random() * this.FAKE_REVISION_NOTES.length)];
    return {
      ...detail,
      information: { ...detail.information, revision_note: detail.information.revision_note || randomNote() },
      target_scope: { ...detail.target_scope, revision_note: detail.target_scope.revision_note || randomNote() },
      target_term: { ...detail.target_term, revision_note: detail.target_term.revision_note || randomNote() },
      program_target_reward: { ...detail.program_target_reward, revision_note: detail.program_target_reward.revision_note || randomNote() },
      discount_terms: detail.discount_terms ? { ...detail.discount_terms, revision_note: detail.discount_terms.revision_note || randomNote() } : null,
      other_terms: detail.other_terms ? { ...detail.other_terms, revision_note: detail.other_terms.revision_note || randomNote() } : null,
      overlap_information_program: detail.overlap_information_program
        ? { ...detail.overlap_information_program, revision_note: detail.overlap_information_program.revision_note || randomNote() }
        : null,
    };
  }

  getDetail(id: string): Observable<BaseResponse<IDetailTargetDistributor>> {
    return this.baseService.getData<IDetailTargetDistributor>(API.TARGET_DISTRIBUTOR.DETAIL + id).pipe(filter((res) => res !== null));
  }

  getDetailEdit(id: string): Observable<BaseResponse<IDetailEditTargetDistributor>> {
    return this.baseService.getData<IDetailEditTargetDistributor>(API.TARGET_DISTRIBUTOR.DETAIL_EDIT + id).pipe(
      filter((res) => res !== null),
      map((res) => (res?.success ? { ...res, data: this.fakeRevisionNoteForDemo(res.data) } : (res as BaseResponse<IDetailEditTargetDistributor>)))
    );
  }

  submitEdit(id: string, payload: ITargetDistributorSubmitRequest): Observable<BaseResponse<{ id: string }>> {
    return this.baseService.postData<{ id: string }>(API.TARGET_DISTRIBUTOR.SUBMIT_EDIT + id, payload);
  }

  submitVerificationStatus(id: string, payload: ITargetDistributorSubmitVerificationRequest): Observable<BaseResponse<{ id: string }>> {
    return this.baseService.postData<{ id: string }>(API.TARGET_DISTRIBUTOR.SUBMIT_VERIFICATION_STATUS + id, payload);
  }

  private readonly SESSION_REVISION_KEY = 'tdRevisionNote';

  setSessionRevisionNote(data: IRevisionNoteTargetDistributor): void {
    sessionStorage.setItem(this.SESSION_REVISION_KEY, JSON.stringify(data));
  }

  getSessionRevisionNote(): IRevisionNoteTargetDistributor | undefined {
    const value = sessionStorage.getItem(this.SESSION_REVISION_KEY);
    if (!value) return undefined;
    return JSON.parse(value);
  }

  clearSessionRevisionNote(): void {
    sessionStorage.removeItem(this.SESSION_REVISION_KEY);
  }

  routeList = () => this.router.navigate([this.basePageUrl + 'list']);

  routeForm = () => this.router.navigate([this.basePageUrl + 'form']);

  routeDetail = (id: string) => this.router.navigate([this.basePageUrl + id]);

  getPrivilegeCTACreate() {
    return this.rolePrivilegeService.checkPrivilege('SALES_MARKETING_PROGRAM', 'TARGET_DISTRIBUTOR_SETTING', 'LIST_TARGET_DISTRIBUTOR', 'CTA_CREATE_PROGRAM_TARGET');
  }

  updateSectionVerification(id: string, payload: any) {
    return this.baseService.postData(`${API.TARGET_DISTRIBUTOR.SETTING.UPDATE_VERIFICATION_STATUS}/${id}`, payload);
  }

  /**
   * Kartu Omzet Program + Estimasi Budget Hadiah pada tab Monitoring.
   * Endpoint menerima filter yang sama dengan tabel, jadi kedua kartu ikut berubah saat search/filter.
   */
  getMonitoringSummary(id: string, params: ITargetDistributorMonitoring__FilterParams): Observable<BaseResponse<ITargetDistributorMonitoring__Summary>> {
    // [OLD - mock] if (this.useMonitoringMock) return of(getMonitoringSummaryMock(params)).pipe(delay(this.MONITORING_MOCK_DELAY_MS));
    return this.baseService.getData<ITargetDistributorMonitoring__Summary>(API.TARGET_DISTRIBUTOR.MONITORING.SUMMARY + id, { disableModalPopupError: true }, { ...params }).pipe(
      filter((res) => res !== null),
      take(1)
    );
  }

  /** Tabel distributor pada tab Monitoring. `page` 0-indexed, `sort` satu string "{field},{direction}". */
  getMonitoringDistributorList(id: string, params: ITargetDistributorMonitoring__ListParams): Observable<BaseResponse<ITargetDistributorMonitoring__Distributor[]>> {
    // [OLD - mock] if (this.useMonitoringMock) return of(getMonitoringDistributorListMock(params)).pipe(delay(this.MONITORING_MOCK_DELAY_MS));
    return this.baseService
      .getData<ITargetDistributorMonitoring__Distributor[]>(API.TARGET_DISTRIBUTOR.MONITORING.DISTRIBUTOR_LIST + id, { disableModalPopupError: true }, { ...params })
      .pipe(
        filter((res) => res !== null),
        take(1)
      );
  }

  /**
   * [MAI-2600] Ringkasan progres satu distributor untuk modal Detail per Distributor.
   *
   * [MAI-2601] Endpoint asli mengirim `data` sebagai objek tunggal, sementara kontrak di API
   * Studio menuliskannya sebagai array. Kedua bentuk dinormalkan di sini supaya pemanggil
   * selalu menerima satu objek (null saat data belum tersedia) dan tetap aman kalau backend
   * berpindah ke bentuk yang satunya.
   */
  getMonitoringDistributorDetail(targetDistributorId: string, distributorId: string): Observable<BaseResponse<ITargetDistributorMonitoring__DistributorDetail | null>> {
    // [OLD - mock] if (this.monitoringMock.distributorDetail) return of(getMonitoringDistributorDetailMock(distributorId)).pipe(delay(this.MONITORING_MOCK_DELAY_MS));

    return this.baseService
      .getData<ITargetDistributorMonitoring__DistributorDetail | ITargetDistributorMonitoring__DistributorDetail[]>(
        `${API.TARGET_DISTRIBUTOR.MONITORING.DISTRIBUTOR_DETAIL}${targetDistributorId}/${distributorId}`,
        {
          disableModalPopupError: true,
        }
      )
      .pipe(
        filter((res) => res !== null),
        take(1),
        map((res) => ({ ...res, data: Array.isArray(res.data) ? (res.data[0] ?? null) : (res.data ?? null) }))
      );
  }

  /**
   * [MAI-2725] Isi modal "Hadiah Didapatkan Distributor" (link "+N Hadiah Lainnya").
   * Endpoint tanpa param — seluruh tingkat dikirim sekaligus, tidak ada pagination.
   */
  getMonitoringOtherRewards(targetDistributorId: string, distributorId: string): Observable<BaseResponse<ITargetDistributorMonitoring__OtherRewards>> {
    // [OLD - mock] if (this.monitoringMock.otherRewards) return of(getMonitoringOtherRewardsMock(distributorId)).pipe(delay(this.MONITORING_MOCK_DELAY_MS));

    return this.baseService
      .getData<ITargetDistributorMonitoring__OtherRewards>(`${API.TARGET_DISTRIBUTOR.MONITORING.OTHER_REWARDS}${targetDistributorId}/${distributorId}`, {
        disableModalPopupError: true,
      })
      .pipe(
        filter((res) => res !== null),
        take(1)
      );
  }

  /** [MAI-2606] Tabel Detail Produk di dalam modal Detail per Distributor. */
  getMonitoringProductList(
    targetDistributorId: string,
    distributorId: string,
    params: ITargetDistributorMonitoring__ProductParams
  ): Observable<BaseResponse<ITargetDistributorMonitoring__Product[]>> {
    // if (this.monitoringMock.productList) return of(getMonitoringProductListMock(params)).pipe(delay(this.MONITORING_MOCK_DELAY_MS));

    return this.baseService
      .getData<
        ITargetDistributorMonitoring__Product[]
      >(`${API.TARGET_DISTRIBUTOR.MONITORING.PRODUCT_LIST}${targetDistributorId}/${distributorId}`, { disableModalPopupError: true }, { ...params })
      .pipe(
        filter((res) => res !== null),
        take(1)
      );
  }

  /**
   * [MAI-2604] Tabel modal "Nilai Invoice [Nama Distributor]".
   * Endpoint hanya menerima `page`/`size` — tidak ada `keyword` maupun `sort`.
   */
  getMonitoringInvoiceList(
    targetDistributorId: string,
    distributorId: string,
    params: ITargetDistributorMonitoring__InvoiceParams
  ): Observable<BaseResponse<ITargetDistributorMonitoring__Invoice[]>> {
    // [OLD - mock] if (this.monitoringMock.invoiceList) return of(getMonitoringInvoiceListMock(params)).pipe(delay(this.MONITORING_MOCK_DELAY_MS));

    return this.baseService
      .getData<
        ITargetDistributorMonitoring__Invoice[]
      >(`${API.TARGET_DISTRIBUTOR.MONITORING.INVOICE_LIST}${targetDistributorId}/${distributorId}`, { disableModalPopupError: true }, { ...params })
      .pipe(
        filter((res) => res !== null),
        take(1)
      );
  }

  /**
   * [MAI-2608] Tabel modal "PO [Nama Distributor]".
   *
   * Kontrak resmi baru memuat `page`/`size`; `sort` tetap dikirim karena AC-02 meminta
   * urutan default nilai PO tertinggi — nilai PO datang sebagai string terformat, jadi
   * pengurutan tidak bisa dilakukan di frontend. Header tabelnya sendiri tidak sortable
   * (QA MAI-2608), jadi nilainya selalu `po_value,desc`.
   */
  getMonitoringPotentialPoList(
    targetDistributorId: string,
    distributorId: string,
    params: ITargetDistributorMonitoring__PotentialPoParams
  ): Observable<BaseResponse<ITargetDistributorMonitoring__PotentialPo[]>> {
    // [OLD - mock] if (this.monitoringMock.potentialPo) return of(getMonitoringPotentialPoListMock(params)).pipe(delay(this.MONITORING_MOCK_DELAY_MS));

    return this.baseService
      .getData<
        ITargetDistributorMonitoring__PotentialPo[]
      >(`${API.TARGET_DISTRIBUTOR.MONITORING.POTENTIAL_PO}${targetDistributorId}/${distributorId}`, { disableModalPopupError: true }, { ...params })
      .pipe(
        filter((res) => res !== null),
        take(1)
      );
  }

  /** Opsi dropdown filter Area. Response memakai {area_id, area_name}, bukan {id, name}. */
  getMonitoringAreaFilter(id: string): Observable<BaseResponse<ITargetDistributorMonitoring__AreaFilter[]>> {
    // [OLD - mock] if (this.monitoringMock.areaFilter) return of(getMonitoringAreaFilterMock()).pipe(delay(this.MONITORING_MOCK_DELAY_MS));
    return this.baseService.getData<ITargetDistributorMonitoring__AreaFilter[]>(API.TARGET_DISTRIBUTOR.MONITORING.FILTER_AREA + id, { disableModalPopupError: true }).pipe(
      filter((res) => res !== null),
      take(1)
    );
  }
}
